import React, { useEffect, useMemo, useState } from 'react';
import { Select, Table, DatePicker, Tag, Empty, Spin, Tooltip, Button, Segmented, Input, InputNumber } from 'antd';
import {
  CheckCircleOutlined, WarningOutlined, ArrowRightOutlined, InboxOutlined, PictureOutlined, AppstoreOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { format } from 'date-fns';
import config from '../../config';
import CataloguePicker from './CataloguePicker';
import { NdoeImage, NdoeResponsiveTable , optionsCouleursParFamille } from '../../components/ndoe';
import '../mobileCockpit/ndoeTokens.css';
import './journalTransactions.css';

const { RangePicker } = DatePicker;
const POINTURES = Array.from({ length: 13 }, (_, i) => 34 + i); // 34..46
// Types d'opération pour le filtre du feed global (ids = type_mouvement).
const TYPE_OPTIONS = [
  { value: 4, label: 'Vente' }, { value: 5, label: 'Retour client' },
  { value: 6, label: 'Échange (entrée)' }, { value: 7, label: 'Échange (sortie)' },
  { value: 10, label: 'Réception' }, { value: 11, label: 'Avarie' },
  { value: 12, label: 'Départ livraison' }, { value: 13, label: 'Retour livraison' },
  { value: 14, label: 'Ajustement +' }, { value: 15, label: 'Ajustement −' },
  { value: 8, label: 'Adressage +' }, { value: 9, label: 'Adressage −' },
];

const toneForType = (entree, sortie) => (entree > 0 ? 'in' : (sortie > 0 ? 'out' : 'trace'));

const JournalTransactions = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const [variantes, setVariantes] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selected, setSelected] = useState(null);
  const [range, setRange] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [model, setModel] = useState(null);
  const [mode, setMode] = useState('article'); // 'article' | 'modele' | 'transactions'
  const [loading, setLoading] = useState(false);
  const [refLists, setRefLists] = useState({ categories: [], marques: [], couleurs: [] });
  const [filters, setFilters] = useState({ id_categorie: null, id_marque: null, id_couleur: null, taille: null });
  const [pickerOpen, setPickerOpen] = useState(false);
  // Vue « toutes les transactions » (feed global paginé côté serveur).
  const [feed, setFeed] = useState(null);
  const [feedPage, setFeedPage] = useState(1);
  const [feedType, setFeedType] = useState(null);
  const [feedQ, setFeedQ] = useState(null);
  const [loadingFeed, setLoadingFeed] = useState(false);
  // Filtres d'investigation du feed (précision) : commande, client, auteur,
  // motif, sens, pointure, couleur. Référentiels auteur/motif chargés à part.
  const [ff, setFf] = useState({ id_commande: null, client: null, id_user: null, motif: null, sens: null, taille: null, id_couleur: null });
  const [staff, setStaff] = useState([]);
  const [motifs, setMotifs] = useState([]);

  // Sélection depuis le popup catalogue : on injecte la paire dans la liste (pour
  // que le sélecteur l'affiche) puis on l'ouvre dans le relevé.
  const handlePick = (v) => {
    setVariantes((prev) => (prev.some((x) => x.id_varianteProduit === v.id_varianteProduit) ? prev : [v, ...prev]));
    setSelected(v.id_varianteProduit);
    setPickerOpen(false);
  };

  // Depuis le feed global : ouvrir le relevé de l'article de la ligne cliquée.
  const openArticle = (r) => {
    if (!r.vid) return;
    setVariantes((prev) => (prev.some((x) => x.id_varianteProduit === r.vid) ? prev
      : [{
        id_varianteProduit: r.vid, code_article: r.code_article, nom_produit: r.nom_produit, couleur: r.couleur, taille: r.taille,
      }, ...prev]));
    setMode('article');
    setSelected(r.vid);
  };

  // Référentiels pour les filtres.
  useEffect(() => {
    (async () => {
      try {
        const [c, m, l] = await Promise.all([
          axios.get(`${DOMAIN}/api/produit/categorie`),
          axios.get(`${DOMAIN}/api/produit/marque`),
          axios.get(`${DOMAIN}/api/produit/couleur`),
        ]);
        setRefLists({
          categories: (c.data || []).map((x) => ({ value: x.id_categorie, label: x.nom_categorie })),
          marques: (m.data || []).map((x) => ({ value: x.id_marque, label: (x.nom || '').trim() })),
          // Repliées par famille : 55 nuances tenaient dans une seule liste.
          couleurs: optionsCouleursParFamille(l.data || [], { avecNbNuances: true }),
        });
      } catch (e) { console.log(e); }
    })();
  }, [DOMAIN]);

  // Référentiels des filtres d'investigation : auteurs (staff) + motifs distincts.
  useEffect(() => {
    (async () => {
      try {
        const [s, m] = await Promise.all([
          axios.get(`${DOMAIN}/api/user/staff`),
          axios.get(`${DOMAIN}/api/journal/motifs`),
        ]);
        setStaff((s.data || []).map((u) => ({ value: u.id, label: u.username })));
        setMotifs((m.data || []).map((code) => ({ value: code, label: code })));
      } catch (e) { console.log(e); }
    })();
  }, [DOMAIN]);

  // Liste des articles (filtrée côté serveur).
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingList(true);
      try {
        const p = new URLSearchParams({ limit: '1000' });
        Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, String(v)); });
        const { data } = await axios.get(`${DOMAIN}/api/catalogue/stock?${p.toString()}`);
        if (alive) setVariantes(Array.isArray(data) ? data : (data?.variantes || []));
      } catch (e) { console.log(e); } finally { if (alive) setLoadingList(false); }
    })();
    return () => { alive = false; };
  }, [DOMAIN, filters]);

  // Relevé de l'article choisi.
  useEffect(() => {
    if (!selected) { setLedger(null); setModel(null); return undefined; }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const p = new URLSearchParams();
        if (range && range[0]) p.set('date_debut', range[0].format('YYYY-MM-DD'));
        if (range && range[1]) p.set('date_fin', range[1].format('YYYY-MM-DD'));
        if (mode === 'modele') {
          const sv = variantes.find((v) => v.id_varianteProduit === selected);
          const idp = sv && sv.id_produit;
          if (idp) { const { data } = await axios.get(`${DOMAIN}/api/journal/produit/${idp}?${p.toString()}`); if (alive) setModel(data); }
        } else {
          const { data } = await axios.get(`${DOMAIN}/api/journal/variante/${selected}?${p.toString()}`);
          if (alive) setLedger(data);
        }
      } catch (e) { console.log(e); } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [selected, range, DOMAIN, mode, variantes]);

  // Feed global (vue par transaction). Pagination + filtres côté serveur.
  useEffect(() => {
    if (mode !== 'transactions') return undefined;
    let alive = true;
    (async () => {
      setLoadingFeed(true);
      try {
        const p = new URLSearchParams({ page: String(feedPage), pageSize: '25' });
        if (filters.id_categorie) p.set('id_categorie', String(filters.id_categorie));
        if (filters.id_marque) p.set('id_marque', String(filters.id_marque));
        if (feedType) p.set('type_id', String(feedType));
        if (feedQ && feedQ.trim()) p.set('q', feedQ.trim());
        if (ff.id_commande) p.set('id_commande', String(ff.id_commande));
        if (ff.client && ff.client.trim()) p.set('client', ff.client.trim());
        if (ff.id_user) p.set('id_user', String(ff.id_user));
        if (ff.motif) p.set('motif', String(ff.motif));
        if (ff.sens) p.set('sens', String(ff.sens));
        if (ff.taille) p.set('taille', String(ff.taille));
        if (ff.id_couleur) p.set('id_couleur', String(ff.id_couleur));
        if (range && range[0]) p.set('date_debut', range[0].format('YYYY-MM-DD'));
        if (range && range[1]) p.set('date_fin', range[1].add(1, 'day').format('YYYY-MM-DD')); // borne incluse
        const { data } = await axios.get(`${DOMAIN}/api/journal/transactions?${p.toString()}`);
        if (alive) setFeed(data);
      } catch (e) { console.log(e); } finally { if (alive) setLoadingFeed(false); }
    })();
    return () => { alive = false; };
  }, [mode, feedPage, filters.id_categorie, filters.id_marque, feedType, feedQ,
    ff.id_commande, ff.client, ff.id_user, ff.motif, ff.sens, ff.taille, ff.id_couleur, range, DOMAIN]);

  const options = useMemo(() => variantes.map((v) => ({
    value: v.id_varianteProduit,
    label: `${v.code_article || '—'}  ·  ${v.nom_produit || ''} · ${v.couleur || '—'} · P${v.taille ?? '?'}`,
  })), [variantes]);

  const article = useMemo(() => variantes.find((v) => v.id_varianteProduit === selected)
    || (ledger ? ledger.variante : null), [variantes, selected, ledger]);
  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v || null }));

  const columns = [
    { title: 'Date', dataIndex: 'occurred_at', key: 'date', width: 116, render: (d) => (d ? <span className="jt-date-cell num">{format(new Date(d), 'dd/MM/yyyy')}</span> : <span className="jt-nil">—</span>) },
    {
      title: 'Opération', dataIndex: 'type_label', key: 'type',
      render: (label, r) => <span className={`jt-type jt-${toneForType(r.entree, r.sortie)}`}>{label || `Type ${r.type_id}`}</span>,
    },
    { title: 'Entrée', dataIndex: 'entree', key: 'entree', align: 'right', width: 86, render: (n) => (n > 0 ? <span className="jt-in num">+{n}</span> : <span className="jt-nil">—</span>) },
    { title: 'Sortie', dataIndex: 'sortie', key: 'sortie', align: 'right', width: 86, render: (n) => (n > 0 ? <span className="jt-out num">−{n}</span> : <span className="jt-nil">—</span>) },
    {
      title: 'Solde comptable', key: 'solde', align: 'right', width: 148,
      render: (_, r) => (
        <span className="jt-solde num">
          {r.impacte_stock ? (
            <><span className="jt-before">{r.comptable_avant}</span><ArrowRightOutlined className="jt-arrow" /><b>{r.comptable_apres}</b></>
          ) : (
            <Tooltip title="Trace logistique — n'affecte pas le stock comptable"><span className="jt-nil">{r.comptable_apres} ·</span></Tooltip>
          )}
          {r.ecart_integrite && <Tooltip title="Écart calculé vs enregistré — à investiguer"><WarningOutlined className="jt-warn" /></Tooltip>}
        </span>
      ),
    },
    {
      title: 'Détail', key: 'detail',
      render: (_, r) => (
        <div className="jt-detail">
          {r.motif_code && <Tag className="jt-motif">{r.motif_code}</Tag>}
          {r.id_commande && <span className="jt-ref">Cmd #{r.id_commande}</span>}
          {r.client_nom && <span className="jt-client">{r.client_nom}</span>}
          {r.source === 'reception' && <span className="jt-ref"><InboxOutlined /> Arrivage</span>}
        </div>
      ),
    },
    { title: 'Auteur', dataIndex: 'auteur', key: 'auteur', width: 104, render: (a) => (a ? <span className="jt-author">{a}</span> : <span className="jt-nil">—</span>) },
  ];

  const view = mode === 'modele' ? model : ledger;
  const stocks = view ? view.stocks : null;
  const declinaisonCol = {
    title: 'Déclinaison', key: 'decl', width: 128,
    render: (_, r) => <span className="jt-decl">{r.variante_couleur || '—'} · P{r.variante_taille ?? '?'}</span>,
  };
  const activeColumns = mode === 'modele' ? [columns[0], columns[1], declinaisonCol, ...columns.slice(2)] : columns;

  // Filtres du feed : changer un critère remet la pagination à la première page.
  const setFeedCat = (v) => { setFeedPage(1); setFilters((f) => ({ ...f, id_categorie: v || null })); };
  const setFeedMar = (v) => { setFeedPage(1); setFilters((f) => ({ ...f, id_marque: v || null })); };
  const setFeedTypeF = (v) => { setFeedPage(1); setFeedType(v || null); };
  const setFeedRange = (v) => { setFeedPage(1); setRange(v); };
  const onFeedSearch = (v) => { setFeedPage(1); setFeedQ(v || null); };
  // Filtres d'investigation : chaque changement remet la pagination à la 1re page.
  const setFF = (k) => (v) => { setFeedPage(1); setFf((f) => ({ ...f, [k]: (v === '' || v === undefined) ? null : v })); };
  const nbFiltres = Object.values(ff).filter((v) => v !== null && v !== '').length
    + [filters.id_categorie, filters.id_marque, feedType, feedQ, range].filter(Boolean).length;
  const resetFeedFiltres = () => {
    setFeedPage(1);
    setFf({ id_commande: null, client: null, id_user: null, motif: null, sens: null, taille: null, id_couleur: null });
    setFilters((f) => ({ ...f, id_categorie: null, id_marque: null }));
    setFeedType(null); setFeedQ(null); setRange(null);
  };

  const feedColumns = [
    { title: 'Date', dataIndex: 'occurred_at', key: 'date', width: 116, render: (d) => (d ? <span className="jt-date-cell num">{format(new Date(d), 'dd/MM/yyyy')}</span> : <span className="jt-nil">—</span>) },
    {
      title: 'Opération', dataIndex: 'type_label', key: 'type', width: 150,
      render: (label, r) => <span className={`jt-type jt-${toneForType(r.entree, r.sortie)}`}>{label || `Type ${r.type_id}`}</span>,
    },
    {
      title: 'Article', key: 'article',
      render: (_, r) => (
        <button type="button" className="jt-artcell" onClick={() => openArticle(r)} disabled={!r.vid} title={r.vid ? 'Voir le relevé de cet article' : ''}>
          <span className="jt-artref">{r.code_article || '—'}</span>
          <span className="jt-artnom">{r.nom_produit}</span>
          <span className="jt-artsub">{r.couleur || '—'} · P{r.taille ?? '?'}</span>
        </button>
      ),
    },
    { title: 'Entrée', dataIndex: 'entree', key: 'entree', align: 'right', width: 86, render: (n) => (n > 0 ? <span className="jt-in num">+{n}</span> : <span className="jt-nil">—</span>) },
    { title: 'Sortie', dataIndex: 'sortie', key: 'sortie', align: 'right', width: 86, render: (n) => (n > 0 ? <span className="jt-out num">−{n}</span> : <span className="jt-nil">—</span>) },
    {
      title: 'Détail', key: 'detail',
      render: (_, r) => (
        <div className="jt-detail">
          {r.motif_code && <Tag className="jt-motif">{r.motif_code}</Tag>}
          {r.id_commande && <span className="jt-ref">Cmd #{r.id_commande}</span>}
          {r.client_nom && <span className="jt-client">{r.client_nom}</span>}
          {r.source === 'reception' && <span className="jt-ref"><InboxOutlined /> Arrivage</span>}
        </div>
      ),
    },
    { title: 'Auteur', dataIndex: 'auteur', key: 'auteur', width: 104, render: (a) => (a ? <span className="jt-author">{a}</span> : <span className="jt-nil">—</span>) },
  ];

  return (
    <div className="jt-page">
      <div className="jt-head">
        <span className="jt-eyebrow">Grand livre · traçabilité</span>
        <h1>Journal des transactions</h1>
        <p>{mode === 'transactions'
          ? 'Toutes les opérations de stock — filtrables, tracées, paginées.'
          : 'Relevé de stock par article — chaque mouvement, son solde, son auteur.'}</p>
      </div>

      <div className="jt-modebar jt-modebar-top">
        <Segmented value={mode} onChange={setMode}
          options={[
            { label: 'Par article', value: 'article' },
            { label: 'Par modèle', value: 'modele' },
            { label: 'Toutes les transactions', value: 'transactions' },
          ]} />
        {mode === 'transactions' && feed
          && <span className="jt-modehint">{feed.total.toLocaleString('fr-FR')} opération{feed.total > 1 ? 's' : ''}</span>}
        {mode === 'modele' && view && view.nb_variantes != null
          && <span className="jt-modehint">{view.nb_variantes} déclinaison{view.nb_variantes > 1 ? 's' : ''} agrégée{view.nb_variantes > 1 ? 's' : ''}</span>}
      </div>

      {mode === 'transactions' ? (
        <>
          <div className="jt-feedfilters">
            <InputNumber className="jt-f jt-f-cmd" placeholder="N° commande" min={1} controls={false} value={ff.id_commande} onChange={setFF('id_commande')} />
            <Input className="jt-f" placeholder="Client" allowClear value={ff.client || ''} onChange={(e) => setFF('client')(e.target.value)} />
            <Select allowClear className="jt-f" placeholder="Auteur" options={staff} value={ff.id_user} onChange={setFF('id_user')} showSearch optionFilterProp="label" />
            <Select allowClear className="jt-f" placeholder="Type d'opération" options={TYPE_OPTIONS} value={feedType} onChange={setFeedTypeF} showSearch optionFilterProp="label" />
            <Select allowClear className="jt-f jt-f-sens" placeholder="Sens" value={ff.sens} onChange={setFF('sens')}
              options={[{ value: 'in', label: '↑ Entrée' }, { value: 'out', label: '↓ Sortie' }, { value: 'trace', label: '· Trace' }]} />
            <Select allowClear className="jt-f" placeholder="Motif" options={motifs} value={ff.motif} onChange={setFF('motif')} showSearch optionFilterProp="label" notFoundContent="Aucun motif" />
            <Select allowClear className="jt-f" placeholder="Catégorie" options={refLists.categories} value={filters.id_categorie} onChange={setFeedCat} showSearch optionFilterProp="label" />
            <Select allowClear className="jt-f" placeholder="Marque" options={refLists.marques} value={filters.id_marque} onChange={setFeedMar} showSearch optionFilterProp="label" />
            <Select allowClear className="jt-f" placeholder="Couleur" options={refLists.couleurs} value={ff.id_couleur} onChange={setFF('id_couleur')} showSearch optionFilterProp="label" />
            <Select allowClear className="jt-f jt-f-pt" placeholder="Pointure" options={POINTURES.map((p) => ({ value: p, label: `P${p}` }))} value={ff.taille} onChange={setFF('taille')} />
            <RangePicker className="jt-range" value={range} onChange={setFeedRange} format="DD/MM/YYYY" placeholder={['Depuis', "Jusqu'à"]} />
            <Input.Search className="jt-feedsearch" placeholder="Réf. ou modèle…" allowClear enterButton defaultValue={feedQ || ''} onSearch={onFeedSearch} />
            {nbFiltres > 0 && <Button className="jt-reset" type="link" onClick={resetFeedFiltres}>Réinitialiser ({nbFiltres})</Button>}
          </div>
          <Spin spinning={loadingFeed}>
            <div className="jt-table-wrap">
              {/* Mobile : liste de cartes déduites des colonnes ci-dessus.
                  Le titre prend la colonne « Opération » plutôt que la 1re (la
                  date, qui ne distingue pas deux lignes du même jour) ; par
                  cardTitreCol, donc elle n'apparaît pas AUSSI en métrique.
                  À savoir : le tri et les filtres de colonne n'existent pas sur
                  la liste mobile — ce journal n'en avait aucun. */}
              <NdoeResponsiveTable
                rowKey={(r) => `${r.source}-${r.ref_id}`} columns={feedColumns} data={feed ? feed.lignes : []}
                loading={loadingFeed} cardTitreCol="type"
                pagination={{
                  current: feedPage, pageSize: feed ? feed.pageSize : 25, total: feed ? feed.total : 0,
                  onChange: (pg) => setFeedPage(pg), showSizeChanger: false,
                  showTotal: (t) => `${t.toLocaleString('fr-FR')} opération${t > 1 ? 's' : ''}`,
                }}
                empty={{ icon: '📄', title: 'Aucune transaction', text: 'Aucune transaction pour ces critères.' }} />
            </div>
          </Spin>
        </>
      ) : (
        <>
      <div className="jt-top">
        <div className="jt-left">
          <div className="jt-filters">
            <Select allowClear className="jt-f" placeholder="Catégorie" options={refLists.categories} value={filters.id_categorie} onChange={setF('id_categorie')} showSearch optionFilterProp="label" />
            <Select allowClear className="jt-f" placeholder="Marque" options={refLists.marques} value={filters.id_marque} onChange={setF('id_marque')} showSearch optionFilterProp="label" />
            <Select allowClear className="jt-f" placeholder="Couleur" options={refLists.couleurs} value={filters.id_couleur} onChange={setF('id_couleur')} showSearch optionFilterProp="label" />
            <Select allowClear className="jt-f jt-f-pt" placeholder="Pointure" options={POINTURES.map((p) => ({ value: p, label: `P${p}` }))} value={filters.taille} onChange={setF('taille')} />
          </div>
          <div className="jt-controls">
            <Select showSearch className="jt-select" placeholder="Choisir un article (réf · modèle · couleur · pointure)…"
              loading={loadingList} options={options} value={selected} onChange={setSelected}
              optionFilterProp="label" filterOption={(i, o) => (o?.label || '').toLowerCase().includes(i.toLowerCase())} allowClear
              notFoundContent={loadingList ? <Spin size="small" /> : 'Aucun article'} />
            <Button className="jt-catbtn" icon={<AppstoreOutlined />} onClick={() => setPickerOpen(true)}>Catalogue</Button>
            <RangePicker className="jt-range" value={range} onChange={setRange} format="DD/MM/YYYY" placeholder={['Depuis', "Jusqu'à"]} />
          </div>
        </div>

        <div className={`jt-imgbox ${article ? 'is-on' : ''}`}>
          {article ? (
            <>
              {/* .jt-imgwrap force 76 px (et non les 72 de size="lg") : taille
                  FIXE, donc largeurReelle suffit — pas besoin de srcset. */}
              <div className="jt-imgwrap"><NdoeImage src={article.img} alt={article.nom_produit || 'Article'} size="lg" largeurReelle={76} /></div>
              <div className="jt-imgmeta">
                <div className="jt-imgref">{article.code_article || '—'}</div>
                <div className="jt-imgnom">{article.nom_produit}</div>
                <div className="jt-imgsub">{article.couleur || '—'} · P{article.taille ?? '?'}</div>
                {stocks && (
                  <div className="jt-imgstocks">
                    <span title="Comptable">Cpt <b>{stocks.comptable}</b></span>
                    <span title="Réservé">Rés <b>{stocks.reserve}</b></span>
                    <span className="jt-dispo" title="Disponible">Dispo <b>{stocks.disponible}</b></span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="jt-imgempty"><PictureOutlined /><span>La paire choisie s'affichera ici</span></div>
          )}
        </div>
      </div>

      {!selected && <div className="jt-empty"><Empty description="Choisissez un article pour afficher son relevé de stock" /></div>}

      {selected && (
        <Spin spinning={loading}>
          {stocks && (
            <>
              <div className="jt-variante">
                <b>{mode === 'modele' ? view.modele.nom_produit : view.variante.nom_produit}</b>
                <span className="jt-vref">{mode === 'modele' ? view.modele.code_produit : view.variante.code_article}</span>
                {view.coherent
                  ? <Tag className="jt-ok" icon={<CheckCircleOutlined />}>Solde cohérent</Tag>
                  : <Tag className="jt-ko" icon={<WarningOutlined />}>Incohérence détectée</Tag>}
              </div>
              <div className="jt-tiles">
                <div className="jt-tile"><span>Stock comptable</span><strong className="num">{stocks.comptable}</strong></div>
                <div className="jt-tile"><span>Réservé</span><strong className="num">{stocks.reserve}</strong></div>
                <div className="jt-tile jt-tile-dispo"><span>Disponible à la vente</span><strong className="num">{stocks.disponible}</strong></div>
                <div className="jt-tile"><span>Avariées</span><strong className="num">{stocks.avariees}</strong></div>
              </div>
            </>
          )}

          {mode === 'modele' && view && view.variantes && (
            <div className="jt-decltable">
              <div className="jt-decltitle">Déclinaisons du modèle</div>
              <div className="jt-table-wrap">
                {/* scroll x : le cadre .jt-table-wrap est en overflow:hidden
                    (journalTransactions.css:99) pour l'arrondi des coins ; sans
                    scroll interne, les colonnes « Disponible » et « Avariées »
                    sont rognées et inatteignables sur téléphone. */}
                <Table rowKey="id_varianteProduit" size="small" pagination={false} scroll={{ x: 'max-content' }} dataSource={view.variantes}
                  columns={[
                    { title: 'Référence', dataIndex: 'code_article', render: (c) => <span className="jt-vref2">{c}</span> },
                    { title: 'Couleur', dataIndex: 'couleur' },
                    { title: 'Pointure', dataIndex: 'taille', align: 'center', render: (t) => `P${t ?? '?'}` },
                    { title: 'Comptable', dataIndex: 'comptable', align: 'right', render: (v2) => <span className="num">{v2}</span> },
                    { title: 'Réservé', dataIndex: 'reserve', align: 'right', render: (v2) => <span className="num">{v2}</span> },
                    { title: 'Disponible', dataIndex: 'disponible', align: 'right', render: (v2) => <b className="num">{v2}</b> },
                    { title: 'Avariées', dataIndex: 'avariees', align: 'right', render: (v2) => <span className="num">{v2}</span> },
                  ]} />
              </div>
            </div>
          )}

          <div className="jt-table-wrap">
            {/* scroll x : même cadre en overflow:hidden que ci-dessus. Ce
                tableau déborde même SANS données ; « Détail » et « Auteur »
                étaient hors d'atteinte au doigt. */}
            <Table rowKey={(r) => `${r.source}-${r.ref_id}`} columns={activeColumns} dataSource={view ? view.lignes : []} size="middle"
              scroll={{ x: 'max-content' }}
              pagination={{ pageSize: 20, hideOnSinglePage: true }} locale={{ emptyText: <Empty description="Aucun mouvement sur la période" /> }} />
          </div>
        </Spin>
      )}
        </>
      )}

      <CataloguePicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={handlePick} refLists={refLists} />
    </div>
  );
};

export default JournalTransactions;
